import { queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { queryKeys } from '@/api/queryKeys';
import { getCurrentUser, getTeamMembers, getTeams } from './api';

export const profileQueryOptions = queryOptions({
  queryKey: queryKeys.user.profile(),
  queryFn: getCurrentUser,
  staleTime: 5 * 60 * 1000,
  // Auth errors won't recover on retry — fail fast so handleApiAuthError can
  // run keycloak.login. Retry once on other failures (transient network).
  retry: (failureCount, error) => {
    const status = (error as AxiosError | undefined)?.response?.status;
    if (status === 401 || status === 403) return false;
    return failureCount < 1;
  },
});

// Outside ProtectedRoute (e.g. /login), don't call this hook.
export function useUserProfile() {
  return useSuspenseQuery(profileQueryOptions).data;
}

export function useTeamMembers() {
  const profile = useUserProfile();
  const teamId = profile.teamId ?? '';
  return useQuery({
    queryKey: queryKeys.user.teamMembers(teamId),
    queryFn: () => getTeamMembers(teamId),
    enabled: !!teamId,
  });
}

export function useTeams() {
  return useQuery({
    queryKey: queryKeys.user.teams(),
    queryFn: getTeams,
  });
}
